import React, { useEffect, useState, useMemo } from "react";
import {
    Box, Typography, Grid, Paper, CircularProgress,
    ToggleButtonGroup, ToggleButton, TextField, Button,
    Table, TableHead, TableBody, TableRow, TableCell, Chip,
    FormControlLabel, Switch
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
    Bar, Line
} from "react-chartjs-2";
import {
    Chart as ChartJS,
    BarElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend
} from "chart.js";
import API_URL from "../config";
import {
    pad, fmtBR, shiftYears, fmtPct, pctChange,
    mergeByLabel, mergeRanking, fetchJson, getRange
} from "../utils/reportHelpers";

ChartJS.register(
    BarElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend
);

// CORES fixas
const COLORS = [
    "#1976d2", "#d32f2f", "#388e3c", "#fbc02d", "#7b1fa2", "#0288d1", "#c2185b",
    "#ff9800", "#43a047", "#ba68c8", "#ff7043", "#00796b"
];

const PRESET_LABELS = {
    este_mes: "Este mês",
    mes_passado: "Mês passado",
    ultimos_3_meses: "Últimos 3 meses",
    este_ano: "Este ano",
    personalizado: "Personalizado"
};
const PRESETS = Object.keys(PRESET_LABELS);

function CardChart({ title, children, empty, height = 300 }) {
    return (
        <Paper elevation={3} sx={{
            minHeight: height + 70,
            minWidth: 260,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            p: 2
        }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>{title}</Typography>
            <Box sx={{
                flex: 1,
                width: "100%",
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}>
                {empty ? (
                    <Typography variant="body2" color="text.secondary">Sem dados</Typography>
                ) : children}
            </Box>
        </Paper>
    );
}

export default function ReportDashboard({ token }) {
    const [preset, setPreset] = useState("este_mes");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");

    const [inicio, fim] = useMemo(
        () => getRange(preset, customStart, customEnd),
        [preset, customStart, customEnd]
    );
    const periodoIncompleto = preset === "personalizado" && (!inicio || !fim);

    // Comparação com o mesmo período do ano anterior (ex.: últimos 3 meses
    // deste ano vs. mesmos 3 meses do ano passado)
    const [compareMode, setCompareMode] = useState(false);
    const [inicio2, fim2] = useMemo(
        () => (compareMode && inicio && fim ? [shiftYears(inicio, -1), shiftYears(fim, -1)] : [null, null]),
        [compareMode, inicio, fim]
    );

    const [summary, setSummary] = useState({});
    const [byUser, setByUser] = useState([]);
    const [byDay, setByDay] = useState([]);
    const [byHour, setByHour] = useState([]);
    const [byStore, setByStore] = useState([]);
    const [byOccurrence, setByOccurrence] = useState([]);
    const [byMonth, setByMonth] = useState([]);
    const [plantoesReport, setPlantoesReport] = useState([]);
    const [loading, setLoading] = useState(true);

    // dados do período comparativo (ano anterior)
    const [summary2, setSummary2] = useState({});
    const [byUser2, setByUser2] = useState([]);
    const [byDay2, setByDay2] = useState([]);
    const [byHour2, setByHour2] = useState([]);
    const [byStore2, setByStore2] = useState([]);
    const [byOccurrence2, setByOccurrence2] = useState([]);
    const [loading2, setLoading2] = useState(false);

    useEffect(() => {
        if (periodoIncompleto) return;

        setLoading(true);
        const qs = inicio && fim ? `?inicio=${inicio}&fim=${fim}` : "";

        // busca cada endpoint separadamente com allSettled: se um endpoint
        // falhar (ex.: backend desatualizado, tabela ausente), os demais
        // ainda são exibidos normalmente em vez de tudo cair pra "sem dados"
        Promise.allSettled([
            fetchJson(`${API_URL}/api/reports/summary${qs}`, token),
            fetchJson(`${API_URL}/api/reports/byUser${qs}`, token),
            fetchJson(`${API_URL}/api/reports/byDay${qs}`, token),
            fetchJson(`${API_URL}/api/reports/byHour${qs}`, token),
            fetchJson(`${API_URL}/api/reports/byStore${qs}`, token),
            fetchJson(`${API_URL}/api/reports/byOccurrence${qs}`, token),
            fetchJson(`${API_URL}/api/reports/byMonth`, token),
            fetchJson(`${API_URL}/api/reports/plantoes${qs}`, token)
        ]).then(([sum, user, day, hour, store, occ, month, plantoes]) => {
            const value = (res, fallback) => {
                if (res.status !== 'fulfilled') {
                    console.error(res.reason);
                    return fallback;
                }
                return res.value;
            };

            setSummary(value(sum, {}));
            setByUser(Array.isArray(value(user, [])) ? value(user, []) : []);
            setByDay((Array.isArray(value(day, [])) ? value(day, []) : []).map(r => {
                const [y, m, d] = r.dia.split('-');
                return { ...r, dia: `${d}/${m}` };
            }));
            const hourRows = Array.isArray(value(hour, [])) ? value(hour, []) : [];
            const hourMap = new Map(hourRows.map(r => [Number(r.hora), Number(r.count)]));
            setByHour(
                Array.from({ length: 24 }, (_, h) => ({
                    hora: `${pad(h)}h`,
                    count: hourMap.get(h) || 0
                }))
            );
            setByStore(Array.isArray(value(store, [])) ? value(store, []) : []);
            setByOccurrence(Array.isArray(value(occ, [])) ? value(occ, []) : []);
            setByMonth((Array.isArray(value(month, [])) ? value(month, []) : []).map(r => {
                const [y, m] = r.mes.split('-');
                return { ...r, mes: `${m}/${y}` };
            }));
            setPlantoesReport(Array.isArray(value(plantoes, [])) ? value(plantoes, []) : []);
            setLoading(false);
        });
    }, [token, inicio, fim, periodoIncompleto]);

    // busca os mesmos dados (exceto hora/mês/plantões) para o período comparativo
    useEffect(() => {
        if (!compareMode || !inicio2 || !fim2) {
            setSummary2({});
            setByUser2([]);
            setByDay2([]);
            setByHour2([]);
            setByStore2([]);
            setByOccurrence2([]);
            return;
        }

        setLoading2(true);
        const qs2 = `?inicio=${inicio2}&fim=${fim2}`;

        Promise.allSettled([
            fetchJson(`${API_URL}/api/reports/summary${qs2}`, token),
            fetchJson(`${API_URL}/api/reports/byUser${qs2}`, token),
            fetchJson(`${API_URL}/api/reports/byDay${qs2}`, token),
            fetchJson(`${API_URL}/api/reports/byHour${qs2}`, token),
            fetchJson(`${API_URL}/api/reports/byStore${qs2}`, token),
            fetchJson(`${API_URL}/api/reports/byOccurrence${qs2}`, token)
        ]).then(([sum, user, day, hour, store, occ]) => {
            const value = (res, fallback) => {
                if (res.status !== 'fulfilled') {
                    console.error(res.reason);
                    return fallback;
                }
                return res.value;
            };

            setSummary2(value(sum, {}));
            setByUser2(Array.isArray(value(user, [])) ? value(user, []) : []);
            setByDay2((Array.isArray(value(day, [])) ? value(day, []) : []).map(r => {
                const [y, m, d] = r.dia.split('-');
                return { ...r, dia: `${d}/${m}` };
            }));
            const hourRows2 = Array.isArray(value(hour, [])) ? value(hour, []) : [];
            const hourMap2 = new Map(hourRows2.map(r => [Number(r.hora), Number(r.count)]));
            setByHour2(
                Array.from({ length: 24 }, (_, h) => ({
                    hora: `${pad(h)}h`,
                    count: hourMap2.get(h) || 0
                }))
            );
            setByStore2(Array.isArray(value(store, [])) ? value(store, []) : []);
            setByOccurrence2(Array.isArray(value(occ, [])) ? value(occ, []) : []);
            setLoading2(false);
        });
    }, [token, compareMode, inicio2, fim2]);

    // tabela de desempenho por atendente combinando os dois períodos (união dos nomes)
    const attendantCompare = useMemo(() => {
        if (!compareMode) return null;
        const map = new Map();
        byUser.forEach(u => map.set(u.atendente, { atendente: u.atendente, count: u.count, avg: u.avg_minutes, count2: 0, avg2: null }));
        byUser2.forEach(u => {
            const existing = map.get(u.atendente) || { atendente: u.atendente, count: 0, avg: null, count2: 0, avg2: null };
            existing.count2 = u.count;
            existing.avg2 = u.avg_minutes;
            map.set(u.atendente, existing);
        });
        return Array.from(map.values()).sort((a, b) => b.count - a.count);
    }, [compareMode, byUser, byUser2]);

    const dayCompare = useMemo(
        () => (compareMode ? mergeByLabel(byDay, byDay2, "dia", "count") : null),
        [compareMode, byDay, byDay2]
    );
    // horas já vêm como 24 posições fixas ("00h".."23h") nos dois períodos,
    // então basta casar pelo rótulo, sem precisar reordenar
    const hourCompare = useMemo(() => {
        if (!compareMode) return null;
        const map2 = new Map(byHour2.map(h => [h.hora, h.count]));
        return {
            labels: byHour.map(h => h.hora),
            dataA: byHour.map(h => h.count),
            dataB: byHour.map(h => map2.get(h.hora) || 0)
        };
    }, [compareMode, byHour, byHour2]);
    const storeCompare = useMemo(
        () => (compareMode ? mergeRanking(byStore, byStore2, "loja", "count") : null),
        [compareMode, byStore, byStore2]
    );
    const occurrenceCompare = useMemo(
        () => (compareMode ? mergeRanking(byOccurrence, byOccurrence2, "ocorrencia", "count") : null),
        [compareMode, byOccurrence, byOccurrence2]
    );

    // Preparação dos datasets para Chart.js
    const lineDay = compareMode && dayCompare
        ? {
            labels: dayCompare.labels,
            datasets: [
                {
                    label: "Período atual",
                    data: dayCompare.dataA,
                    fill: false,
                    borderColor: "#1976d2",
                    tension: 0.3,
                    pointBackgroundColor: "#1976d2"
                },
                {
                    label: "Mesmo período (ano anterior)",
                    data: dayCompare.dataB,
                    fill: false,
                    borderColor: "#9e9e9e",
                    borderDash: [5, 5],
                    tension: 0.3,
                    pointBackgroundColor: "#9e9e9e"
                }
            ]
        }
        : {
            labels: byDay.map(i => i.dia),
            datasets: [{
                label: "Atendimentos",
                data: byDay.map(i => i.count),
                fill: false,
                borderColor: "#1976d2",
                tension: 0.3,
                pointBackgroundColor: "#1976d2"
            }]
        };
    const lineHour = compareMode && hourCompare
        ? {
            labels: hourCompare.labels,
            datasets: [
                {
                    label: "Período atual",
                    data: hourCompare.dataA,
                    fill: false,
                    borderColor: "#388e3c",
                    tension: 0.3,
                    pointBackgroundColor: "#388e3c"
                },
                {
                    label: "Mesmo período (ano anterior)",
                    data: hourCompare.dataB,
                    fill: false,
                    borderColor: "#9e9e9e",
                    borderDash: [5, 5],
                    tension: 0.3,
                    pointBackgroundColor: "#9e9e9e"
                }
            ]
        }
        : {
            labels: byHour.map(i => i.hora),
            datasets: [{
                label: "Atendimentos",
                data: byHour.map(i => i.count),
                fill: false,
                borderColor: "#388e3c",
                tension: 0.3,
                pointBackgroundColor: "#388e3c"
            }]
        };
    const lineMonth = {
        labels: byMonth.map(i => i.mes),
        datasets: [{
            label: "Atendimentos",
            data: byMonth.map(i => i.count),
            fill: false,
            borderColor: "#fbc02d",
            tension: 0.3,
            pointBackgroundColor: "#fbc02d"
        }]
    };
    // Barras horizontais rankeadas (top 10, já vem ordenado do backend) -
    // mais fáceis de ler e comparar do que pizza, e comportam nomes longos.
    // Em modo comparação, viram duas séries (atual x ano anterior) lado a lado.
    const barStore = compareMode && storeCompare
        ? {
            labels: storeCompare.labels,
            datasets: [
                { label: "Período atual", data: storeCompare.dataA, backgroundColor: "#1976d2" },
                { label: "Ano anterior", data: storeCompare.dataB, backgroundColor: "#9e9e9e" }
            ]
        }
        : {
            labels: byStore.map(i => i.loja),
            datasets: [{
                label: "Atendimentos",
                data: byStore.map(i => i.count),
                backgroundColor: COLORS
            }]
        };
    const barOccurrence = compareMode && occurrenceCompare
        ? {
            labels: occurrenceCompare.labels,
            datasets: [
                { label: "Período atual", data: occurrenceCompare.dataA, backgroundColor: "#1976d2" },
                { label: "Ano anterior", data: occurrenceCompare.dataB, backgroundColor: "#9e9e9e" }
            ]
        }
        : {
            labels: byOccurrence.map(i => i.ocorrencia),
            datasets: [{
                label: "Atendimentos",
                data: byOccurrence.map(i => i.count),
                backgroundColor: COLORS
            }]
        };
    const horizontalBarOptions = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: compareMode } },
        scales: {
            x: { ticks: { font: { size: 12 } }, beginAtZero: true },
            y: { ticks: { font: { size: 12 } } }
        }
    };

    // Gera o PDF a partir da impressão do navegador (Ctrl+P / "Salvar como PDF"),
    // mostrando só o conteúdo dentro de #report-print-area (a partir do Resumo,
    // sem o título e sem o filtro de período).
    const handleExportPdf = () => {
        const prevTitle = document.title;
        const parts = ["Relatorio"];
        if (inicio && fim) parts.push(`${inicio}_a_${fim}`);
        document.title = parts.join("_");
        window.print();
        setTimeout(() => { document.title = prevTitle; }, 300);
    };

    return (
        <Box sx={{ width: "100%", minHeight: "100vh", overflowX: "hidden" }}>
            {/* Estilos de impressão: ao imprimir/gerar PDF, some com tudo que não
                estiver dentro de #report-print-area (título e filtro ficam de fora) */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    #report-print-area, #report-print-area * { visibility: visible; }
                    #report-print-area {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        padding: 16px;
                    }
                    /* PDF sempre em fundo branco e texto escuro, mesmo com o tema escuro ativo */
                    html, body { background: #fff !important; }
                    #report-print-area {
                        background: #fff !important;
                        color: #111 !important;
                    }
                    #report-print-area .MuiPaper-root {
                        background: #fff !important;
                        background-image: none !important;
                        color: #111 !important;
                        box-shadow: none !important;
                        border: 1px solid #ddd;
                        break-inside: avoid;
                    }
                    #report-print-area .MuiTypography-root,
                    #report-print-area .MuiTableCell-root {
                        color: #111 !important;
                        border-color: #ddd !important;
                    }
                    @page { margin: 12mm; }
                }
            `}</style>

            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                ml: { xs: 2, sm: 4 },
                mr: { xs: 2, sm: 4 },
                mt: 2
            }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                    Painel de Relatórios
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleExportPdf}
                    disabled={loading || periodoIncompleto}
                >
                    Exportar PDF
                </Button>
            </Box>

            {/* Filtro de período */}
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
                px: { xs: 2, sm: 3 },
                mb: 2
            }}>
                <ToggleButtonGroup
                    value={preset}
                    exclusive
                    size="small"
                    onChange={(_, v) => v && setPreset(v)}
                >
                    {PRESETS.map(p => (
                        <ToggleButton key={p} value={p}>{PRESET_LABELS[p]}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {preset === "personalizado" && (
                    <>
                        <TextField
                            label="De"
                            type="date"
                            size="small"
                            value={customStart}
                            onChange={e => setCustomStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Até"
                            type="date"
                            size="small"
                            value={customEnd}
                            onChange={e => setCustomEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </>
                )}
                <FormControlLabel
                    control={
                        <Switch
                            checked={compareMode}
                            onChange={e => setCompareMode(e.target.checked)}
                            disabled={periodoIncompleto}
                        />
                    }
                    label="Comparar com mesmo período do ano anterior"
                />
            </Box>

            {periodoIncompleto ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 2, sm: 3 } }}>
                    Selecione as duas datas para ver o relatório do período personalizado.
                </Typography>
            ) : loading || (compareMode && loading2) ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box id="report-print-area">
                    {/* Período filtrado (aparece na tela e no PDF, já que o filtro em si fica de fora) */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ px: { xs: 2, sm: 3 }, mb: compareMode ? 0.5 : 1 }}>
                        Período: {inicio === fim ? fmtBR(inicio) : `${fmtBR(inicio)} a ${fmtBR(fim)}`} ({PRESET_LABELS[preset]})
                    </Typography>
                    {compareMode && (
                        <Typography variant="subtitle2" color="text.secondary" sx={{ px: { xs: 2, sm: 3 }, mb: 1 }}>
                            Comparando com: {inicio2 === fim2 ? fmtBR(inicio2) : `${fmtBR(inicio2)} a ${fmtBR(fim2)}`} (mesmo período, ano anterior)
                        </Typography>
                    )}

                    {/* Cards de resumo */}
                    <Grid container spacing={2} mb={1} sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 } }}>
                        {[
                            {
                                title: "Tempo Médio",
                                value: summary?.averageTime ? `${summary.averageTime} min` : "-",
                                prevValue: summary2?.averageTime ? `${summary2.averageTime} min` : "-",
                                pct: pctChange(summary?.averageTime, summary2?.averageTime)
                            },
                            {
                                title: "Total Atend.",
                                value: summary?.total || 0,
                                prevValue: summary2?.total || 0,
                                pct: pctChange(summary?.total, summary2?.total)
                            },
                            {
                                title: "Top Atendente",
                                value: summary?.topAttendant ? `${summary.topAttendant} (${summary.topCount})` : "-",
                                prevValue: summary2?.topAttendant ? `${summary2.topAttendant} (${summary2.topCount})` : "-",
                                pct: null
                            }
                        ].map((c, i) => (
                            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                                <Paper elevation={3} sx={{ p: 2, mb: 1 }}>
                                    <Typography variant="subtitle1">{c.title}</Typography>
                                    <Typography variant="h4">{c.value}</Typography>
                                    {compareMode && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Ano anterior: {c.prevValue}
                                            </Typography>
                                            {c.pct !== null && (
                                                <Chip size="small" label={fmtPct(c.pct)} />
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Plantões de sábado: prestação de contas */}
                    <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
                        <Paper elevation={3} sx={{ p: 2, overflowX: "auto" }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                                Plantões de sábado — prestação de contas
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                "Total acumulado" considera todos os plantões já
                                realizados (fonte: Agenda), não só o período selecionado.
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Colaborador</TableCell>
                                        <TableCell align="center">Plantões no período</TableCell>
                                        <TableCell align="center">Total acumulado</TableCell>
                                        <TableCell align="center">Auxílios devidos</TableCell>
                                        <TableCell align="center">Faltam p/ próximo</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {plantoesReport.map(p => (
                                        <TableRow key={p.username}>
                                            <TableCell>{p.username}</TableCell>
                                            <TableCell align="center">{p.plantoesPeriodo}</TableCell>
                                            <TableCell align="center">{p.totalAcumulado}</TableCell>
                                            <TableCell align="center">
                                                {p.auxiliosDevidos > 0 ? (
                                                    <Chip size="small" color="success" label={p.auxiliosDevidos} />
                                                ) : 0}
                                            </TableCell>
                                            <TableCell align="center">{p.faltamProximo}</TableCell>
                                        </TableRow>
                                    ))}
                                    {plantoesReport.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography variant="body2" color="text.secondary">Nenhum colaborador cadastrado.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>

                    {/* Desempenho por atendente */}
                    <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
                        <Paper elevation={3} sx={{ p: 2, overflowX: "auto" }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                                Desempenho por atendente (período selecionado)
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Atendente</TableCell>
                                        <TableCell align="center">Qtd. atendimentos</TableCell>
                                        <TableCell align="center">Tempo médio</TableCell>
                                        {compareMode && (
                                            <>
                                                <TableCell align="center">Qtd. (ano anterior)</TableCell>
                                                <TableCell align="center">Tempo médio (ano anterior)</TableCell>
                                                <TableCell align="center">Variação</TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(compareMode ? attendantCompare : byUser).map(u => (
                                        <TableRow key={u.atendente}>
                                            <TableCell>{u.atendente}</TableCell>
                                            <TableCell align="center">{u.count}</TableCell>
                                            <TableCell align="center">
                                                {u.avg_minutes !== null && u.avg_minutes !== undefined ? `${u.avg_minutes} min` : (u.avg !== null && u.avg !== undefined ? `${u.avg} min` : '-')}
                                            </TableCell>
                                            {compareMode && (
                                                <>
                                                    <TableCell align="center">{u.count2 || 0}</TableCell>
                                                    <TableCell align="center">
                                                        {u.avg2 !== null && u.avg2 !== undefined ? `${u.avg2} min` : '-'}
                                                    </TableCell>
                                                    <TableCell align="center">{fmtPct(pctChange(u.count, u.count2))}</TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    ))}
                                    {(compareMode ? attendantCompare : byUser).length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={compareMode ? 6 : 3} align="center">
                                                <Typography variant="body2" color="text.secondary">Sem atendimentos no período.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>

                    {/* Linha 1 de gráficos */}
                    <Grid container spacing={2} mb={1} sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 } }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <CardChart title={compareMode ? "Atendimentos por Dia (atual x ano anterior)" : "Atendimentos por Dia (período selecionado)"} empty={!byDay?.length && !byDay2?.length}>
                                {(byDay?.length > 0 || byDay2?.length > 0) &&
                                    <Line data={lineDay} options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: { legend: { display: compareMode } },
                                        scales: { x: { ticks: { font: { size: 12 } } }, y: { ticks: { font: { size: 12 } } } }
                                    }} height={250} />
                                }
                            </CardChart>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <CardChart
                                title={compareMode ? "Atendimentos por Hora (atual x ano anterior)" : "Atendimentos por Hora (pico do dia)"}
                                empty={!byHour?.some(h => h.count > 0) && !byHour2?.some(h => h.count > 0)}
                            >
                                {(byHour?.some(h => h.count > 0) || byHour2?.some(h => h.count > 0)) &&
                                    <Line data={lineHour} options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: { legend: { display: compareMode } },
                                        scales: { x: { ticks: { font: { size: 11 }, maxRotation: 0, autoSkip: true } }, y: { ticks: { font: { size: 12 } } } }
                                    }} height={250} />
                                }
                            </CardChart>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <CardChart title="Evolução Mensal (últimos 12 meses)" empty={!byMonth?.length}>
                                {byMonth?.length > 0 &&
                                    <Line data={lineMonth} options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: { legend: { display: false } },
                                        scales: { x: { ticks: { font: { size: 12 } } }, y: { ticks: { font: { size: 12 } } } }
                                    }} height={250} />
                                }
                            </CardChart>
                        </Grid>
                    </Grid>

                    {/* Linha 2 de gráficos: rankings (top 10) em barras horizontais */}
                    <Grid container spacing={2} sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 }, pb: 2 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <CardChart title={compareMode ? "Top Lojas (atual x ano anterior)" : "Top Lojas"} empty={!byStore?.length && !byStore2?.length} height={340}>
                                {(byStore?.length > 0 || byStore2?.length > 0) &&
                                    <Bar data={barStore} options={horizontalBarOptions} />
                                }
                            </CardChart>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <CardChart title={compareMode ? "Top Ocorrências (atual x ano anterior)" : "Top Ocorrências"} empty={!byOccurrence?.length && !byOccurrence2?.length} height={340}>
                                {(byOccurrence?.length > 0 || byOccurrence2?.length > 0) &&
                                    <Bar data={barOccurrence} options={horizontalBarOptions} />
                                }
                            </CardChart>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
}
